import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PwaInstallBanner from "./PwaInstallBanner";
import IosInstallInstructionsModal from "./IosInstallInstructionsModal";

export const PWA_INSTALL_DISMISS_KEY = "PwaInstallDismissedUntilByClub";
export const PWA_INSTALL_DISMISS_DAYS = 7;
const PWA_ANDROID_AUTO_INVITE_DELAY_MS = 1500;

const DISMISS_MS = PWA_INSTALL_DISMISS_DAYS * 24 * 60 * 60 * 1000;

const PwaInstallContext = createContext({
  canInstallApp: false,
  isBannerVisible: false,
  isInstalled: false,
  isIos: false,
  isIosSafari: false,
  installApp: () => {},
  dismissBanner: () => {},
  openIosInstructions: () => {},
  closeIosInstructions: () => {},
});

const isBrowser = () => typeof window !== "undefined";

const getClubStorageId = (clubId) =>
  clubId !== undefined && clubId !== null ? String(clubId) : "default";

const readDismissMap = () => {
  if (!isBrowser()) return {};

  try {
    const raw = window.localStorage?.getItem(PWA_INSTALL_DISMISS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getDismissedUntil = (clubId) => {
  const value = readDismissMap()[getClubStorageId(clubId)];
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

const saveDismissedUntil = (clubId, dismissedUntil) => {
  if (!isBrowser()) return;

  try {
    const next = {
      ...readDismissMap(),
      [getClubStorageId(clubId)]: dismissedUntil,
    };
    window.localStorage?.setItem(PWA_INSTALL_DISMISS_KEY, JSON.stringify(next));
  } catch {
    // Storage availability is best-effort only.
  }
};

const isStandaloneDisplay = () => {
  if (!isBrowser()) return false;

  const standaloneMedia = window.matchMedia?.("(display-mode: standalone)")
    ?.matches;
  const iosStandalone = Boolean(window.navigator?.standalone);
  return Boolean(standaloneMedia || iosStandalone);
};

const detectClient = () => {
  if (!isBrowser()) {
    return {
      isMobile: false,
      isAndroid: false,
      isIos: false,
      isIosSafari: false,
      isStandalone: false,
    };
  }

  const nav = window.navigator || {};
  const ua = nav.userAgent || "";
  const platform = nav.platform || "";
  const maxTouchPoints = nav.maxTouchPoints || 0;
  const isIpadOS = platform === "MacIntel" && maxTouchPoints > 1;
  const isIos = /iPad|iPhone|iPod/.test(ua) || isIpadOS;
  const isAndroid = /Android/i.test(ua);
  const isMobileViewport = window.matchMedia?.("(max-width: 768px)")?.matches;
  const isTouchMobileViewport = Boolean(isMobileViewport && maxTouchPoints > 0);
  const isMobile = Boolean(isAndroid || isIos || isTouchMobileViewport);
  const isCriOS = /CriOS/i.test(ua);
  const isFxiOS = /FxiOS/i.test(ua);
  const isEdgiOS = /EdgiOS/i.test(ua);
  const isOperaIOS = /OPiOS/i.test(ua);
  const hasSafari = /Safari/i.test(ua);
  const isIosSafari = Boolean(
    isIos && hasSafari && !isCriOS && !isFxiOS && !isEdgiOS && !isOperaIOS,
  );

  return {
    isMobile,
    isAndroid,
    isIos,
    isIosSafari,
    isStandalone: isStandaloneDisplay(),
  };
};

export const PwaInstallProvider = ({
  children,
  club,
  bottomOffset = 24,
  hasReachedClubHome = false,
}) => {
  const [client, setClient] = useState(() => detectClient());
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => client.isStandalone);
  const [dismissedUntil, setDismissedUntil] = useState(0);
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [homeWasReached, setHomeWasReached] = useState(false);
  const [promptConsumedInSession, setPromptConsumedInSession] = useState(false);
  const [autoInviteVisible, setAutoInviteVisible] = useState(false);
  const [autoInviteShownInSession, setAutoInviteShownInSession] = useState(false);

  const clubId = club?.id ?? null;

  useEffect(() => {
    setDismissedUntil(getDismissedUntil(clubId));
  }, [clubId]);

  useEffect(() => {
    if (hasReachedClubHome) {
      setHomeWasReached(true);
    }
  }, [hasReachedClubHome]);

  useEffect(() => {
    if (!isBrowser()) return undefined;

    const refreshClient = () => {
      const nextClient = detectClient();
      setClient(nextClient);
      if (nextClient.isStandalone) {
        setIsInstalled(true);
      }
    };

    refreshClient();

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPromptEvent(event);
      setPromptConsumedInSession(false);
      setAutoInviteVisible(false);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPromptEvent(null);
      setIosModalOpen(false);
      setPromptConsumedInSession(false);
      setAutoInviteVisible(false);
      setAutoInviteShownInSession(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    window.addEventListener("resize", refreshClient);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("resize", refreshClient);
    };
  }, []);

  const openIosInstructions = useCallback(() => {
    if (!client.isIos || isInstalled) return;
    setIosModalOpen(true);
  }, [client.isIos, isInstalled]);

  const closeIosInstructions = useCallback(() => {
    setIosModalOpen(false);
  }, []);

  const dismissBanner = useCallback(() => {
    const nextDismissedUntil = Date.now() + DISMISS_MS;
    saveDismissedUntil(clubId, nextDismissedUntil);
    setDismissedUntil(nextDismissedUntil);
    setAutoInviteVisible(false);
    setAutoInviteShownInSession(true);
  }, [clubId]);

  const installApp = useCallback(async () => {
    if (isInstalled) return { outcome: "installed" };

    if (client.isIos) {
      setIosModalOpen(true);
      return { outcome: "instructions" };
    }

    if (!installPromptEvent) {
      return { outcome: "unavailable" };
    }

    const promptEvent = installPromptEvent;
    setInstallPromptEvent(null);
    setPromptConsumedInSession(true);
    promptEvent.prompt();

    try {
      const choice = await promptEvent.userChoice;
      if (choice?.outcome === "accepted") {
        setIsInstalled(true);
      }
      return choice || { outcome: "unknown" };
    } catch {
      return { outcome: "error" };
    }
  }, [client.isIos, installPromptEvent, isInstalled]);

  const canInstallApp = useMemo(() => {
    if (!client.isMobile || isInstalled) return false;
    if (client.isIos) return true;
    return Boolean(installPromptEvent) && !promptConsumedInSession;
  }, [
    client.isIos,
    client.isMobile,
    installPromptEvent,
    isInstalled,
    promptConsumedInSession,
  ]);

  const canShowAutomaticInvite = useMemo(() => {
    if (!homeWasReached) return false;
    if (!canInstallApp) return false;
    if (!client.isAndroid) return false;
    if (client.isStandalone || isInstalled) return false;
    if (dismissedUntil && dismissedUntil > Date.now()) return false;
    return true;
  }, [
    canInstallApp,
    client.isAndroid,
    client.isStandalone,
    dismissedUntil,
    homeWasReached,
    isInstalled,
  ]);

  useEffect(() => {
    if (!canShowAutomaticInvite) {
      setAutoInviteVisible(false);
      return undefined;
    }

    if (autoInviteShownInSession || autoInviteVisible) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setAutoInviteVisible(true);
      setAutoInviteShownInSession(true);
    }, PWA_ANDROID_AUTO_INVITE_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [
    autoInviteShownInSession,
    autoInviteVisible,
    canShowAutomaticInvite,
  ]);

  const isBannerVisible = useMemo(() => {
    if (client.isAndroid) return autoInviteVisible;
    if (!homeWasReached) return false;
    if (!canInstallApp) return false;
    if (dismissedUntil && dismissedUntil > Date.now()) return false;
    return true;
  }, [
    autoInviteVisible,
    canInstallApp,
    client.isAndroid,
    dismissedUntil,
    homeWasReached,
  ]);

  const contextValue = useMemo(
    () => ({
      canInstallApp,
      isAndroid: client.isAndroid,
      isBannerVisible,
      isInstalled,
      isIos: client.isIos,
      isIosSafari: client.isIosSafari,
      isMobile: client.isMobile,
      installApp,
      dismissBanner,
      openIosInstructions,
      closeIosInstructions,
    }),
    [
      canInstallApp,
      client.isAndroid,
      client.isIos,
      client.isIosSafari,
      client.isMobile,
      closeIosInstructions,
      dismissBanner,
      installApp,
      isBannerVisible,
      isInstalled,
      openIosInstructions,
    ],
  );

  return (
    <PwaInstallContext.Provider value={contextValue}>
      {children}
      <PwaInstallBanner
        club={club}
        bottomOffset={bottomOffset}
        open={isBannerVisible}
        onDismiss={dismissBanner}
        onInstall={installApp}
      />
      <IosInstallInstructionsModal
        open={iosModalOpen}
        clubName={club?.name}
        isSafari={client.isIosSafari}
        onClose={closeIosInstructions}
      />
    </PwaInstallContext.Provider>
  );
};

PwaInstallProvider.propTypes = {
  bottomOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.any,
  club: PropTypes.object,
  hasReachedClubHome: PropTypes.bool,
};

export const usePwaInstall = () => useContext(PwaInstallContext);

export default PwaInstallContext;
